/**
 * # dxfBridge.drawingToDxf
 *
 * Reverse-direction bridge: DXF → DrawingModel.
 *
 * Owns the parser-side converters that take freshly parsed DxfSupportedEntity
 * records (plus style/layer/MLEADERSTYLE entries from the TABLES / OBJECTS
 * sections) and returns the strongly-typed DrawingModel shapes used by the
 * renderer, editor, and command engine.
 *
 * Split from the monolithic `dxfBridge.ts` to respect the CLAUDE.md
 * 2000-line guardrail. All public entry points remain re-exported from
 * `dxfBridge.ts` so consumers don't need to update their imports.
 *
 * ## Pipeline map (reverse direction)
 *
 *   toDrawingEntity(dxf) -> uses toDrawingPoint / toDrawingVertex /
 *                           toDrawingHatchEdge / parseArrayXData from
 *                           ./dxfBridge.entityConversion, and the heavy-
 *                           POLYLINE child-record projectors
 *                           (toPolylineVertexRecord /
 *                           toPolylineSeqendMetadata) from
 *                           ./dxfBridge.polylineChildRecords, which this
 *                           module re-exports
 *
 *   dxfTextStyleToDrawingTextStyle  / dxfDimStyleToDrawingDimStyle  /
 *   dxfMLeaderStyleToDrawingMLeaderStyle  (style-table decoders)
 *
 *   extractAttdefFromGroups (block-child fallback decoder)
 *   extractAttribsFromSequence (legacy ATTRIB sequence decoder)
 *
 *   buildDefaultStandard{Text,Dimension,Multileader}Style — schema-seeded
 *   fallback constructors used when the parsed DXF omits "Standard".
 */

import {
  type DxfBlockDefinition,
  type DxfDimensionStyleEntry,
  type DxfHatchBoundaryPath,
  type DxfMLeaderContext,
  type DxfMLeaderEntity,
  type DxfMultileaderStyleEntry,
  type DxfSupportedEntity,
  type DxfTextStyleEntry,
  type DxfXDataEntry,
  DIM_VAR_BY_GROUP_CODE,
  DIM_BLOCK_EXCLUSIVE,
} from "@dxf-ai-editor/dxf-io";

import {
  toPolylineSeqendMetadata,
  toPolylineVertexRecord,
} from "./dxfBridge.polylineChildRecords";
import {
  dxf171ToModelLeaderLineWeight,
  parseArrayXData,
  toDrawingHatchEdge,
  toDrawingPoint,
  toDrawingPoint3D,
  toDrawingVertex,
} from "./dxfBridge.entityConversion";
import { colorFromDxf } from "./color";
import { readDimensionTextMirrored } from "./dimensionTextMirroredXData";
import { dxfEllipseToDrawingEllipse } from "./dxfBridge.ellipse";
import { dxfSplineToDrawingSpline } from "./dxfBridge.spline";
import { readViewportOverrides } from "./viewportOverrides";
// GitHub Codex review PR #558 (post-rebase fresh pass) P2 — the SAME
// epsilon-tolerant OCS-mirror predicate the export side
// (`dxfBridge.mleaderContextConversion.ts`'s `modelMLeaderContextToDxf`)
// uses, imported (not duplicated) so both directions classify a
// near-zero-but-negative extrusion Z identically — see that module's own
// doc for the byte-faithfulness rationale. One-directional import (this
// file → that one); the reverse edge does not exist, so no cycle.
import { isMLeaderMTextMirroredExtrusion } from "./dxfBridge.mleaderContextConversion";

import {
  dimensionStyleSchema,
  mleaderBlockDataSchema,
  mleaderContextSchema,
  mleaderMTextDataSchema,
  multileaderStyleSchema,
  textStyleSchema,
  type ArcEntity,
  type AttdefEntity,
  type BlockInsertAttribute,
  type BlockInsertEntity,
  type CircleEntity,
  type DimensionEntity,
  type DimensionStyle,
  type DimensionStyleOverrides,
  type DrawingEntity,
  type DrawingPoint3D,
  type HatchBoundaryPath,
  type HatchEntity,
  type LineEntity,
  type LwpolylineEntity,
  type MtextEntity,
  type MultileaderEntity,
  type OleFrameEntity,
  type PolylineEntity,
  type TableEntity,
  type MultileaderStyle,
  type PointEntity,
  type RevisionCloudEntity,
  type WipeoutEntity,
  type TextEntity,
  type TextStyle,
  type ViewportEntity,
  type ArrayRectEntity,
} from "./model";

// Re-export so consumers using `DxfBlockDefinition` from the bridge
// layer don't need to reach into @dxf-ai-editor/dxf-io directly; used by
// dxfBridge.import.ts via the barrel.
export type { DxfBlockDefinition };


// S-EXTRUSION-STORE — import helper for the DXF OCS normal (group
// 210/220/230) on the entities that store it as an *optional* model field
// (Line/Point/Circle/Arc/LWPOLYLINE/Attdef). The dxf-io entity parser puts
// the parsed normal on `draft.common.extrusion`. We only carry a NON-default
// value onto the typed model — an absent or identity (0,0,1) extrusion maps
// to no field at all (absent ⇒ the default), keeping models compact and
// matching how Text/Ellipse/Block read their extrusion. Returns a spreadable
// `{ extrusion } | {}` object so the entity-case literals stay terse.
function extrusionField(
  parsed: { x: number; y: number; z: number } | undefined,
): { extrusion: DrawingPoint3D } | Record<string, never> {
  if (
    parsed &&
    !(parsed.x === 0 && parsed.y === 0 && parsed.z === 1)
  ) {
    return { extrusion: toDrawingPoint3D(parsed) };
  }
  return {};
}

// Narrow the transport's plain-string REVCLOUD creation mode to the
// model schema's enum. AutoCAD's REVCLOUD command modes map 1:1
// (Rectangular / Polygonal / Object). Any unrecognised value falls back
// to "Rectangular" at the call site.
function isRevcloudCreationMode(
  value: string | undefined,
): value is "Rectangular" | "Polygonal" | "Object" {
  return (
    value === "Rectangular" || value === "Polygonal" || value === "Object"
  );
}

// ── S-MLEADER-CONTEXT-STRUCTURAL (sub-slice D) — transport→model CONTEXT_DATA ──
//
// Symmetric inverse of `modelMLeaderContextToDxf` (dxfBridge.entityConversion).
// The transport (DxfMLeaderContext) and the drawing-model schema carry IDENTICAL
// field names and structurally-identical points, so a spread feeds the Zod
// schema directly (which applies the AutoCAD-parity defaults + validates the
// nested INLINE MTextData / BlockData). The leader hierarchy is projected by
// `dxfMLeaderLeaderRootsToModel`.

/**
 * Whether a CONTEXT_DATA block carries ANY typed data field worth projecting
 * onto the typed model.
 *
 * The earlier (false-negative) gate enumerated only ~10 of the ~19 typed
 * `DxfMLeaderContext` fields (scale / basePoint / charHeight / plane + the
 * MTextData / BlockData presence). A context carrying ONLY a non-enumerated
 * field — arrowHeadSize (140) / landingGapSize (145) / textLeftAttachment (174)
 * / textRightAttachment (175) / textAlignType (176) / attachmentType (177) /
 * topAttachment (272) / bottomAttachment (273) / planeNormalReversed (297) —
 * returned false, so `contextData` (and its rawContextGroups) was dropped at
 * import (those substantive fields silently lost). This now enumerates the FULL
 * typed field set. `hasMText`/`hasBlock` count when present (`!== undefined`)
 * like every other field: since #294 r2 the parser sets them only when the
 * source carried group 290/296 (no pre-initialised `false`), so presence —
 * including an explicit `290 0` / `296 0` — is substantive source data.
 *
 * The call site additionally treats a non-empty `rawContextGroups` as
 * substantive (a context whose only content is genuinely-unrecognised groups
 * must still survive verbatim — ПРИОРИТЕТ #1). A context with ZERO typed fields
 * AND empty rawContextGroups (a pure marker-only synthetic — the #243
 * degenerate case) is the only one rejected; it round-trips through the writer's
 * fallback path, whose group-340 stamp the faithful path now also emits.
 */
function isSubstantiveMLeaderContext(ctx: DxfMLeaderContext): boolean {
  return (
    ctx.hasMText !== undefined ||
    ctx.hasBlock !== undefined ||
    ctx.mTextData !== undefined ||
    ctx.blockData !== undefined ||
    ctx.scale !== undefined ||
    ctx.basePoint !== undefined ||
    ctx.charHeight !== undefined ||
    ctx.arrowHeadSize !== undefined ||
    ctx.landingGapSize !== undefined ||
    ctx.textLeftAttachment !== undefined ||
    ctx.textRightAttachment !== undefined ||
    ctx.textAlignType !== undefined ||
    ctx.attachmentType !== undefined ||
    ctx.planeOrigin !== undefined ||
    ctx.planeXAxis !== undefined ||
    ctx.planeYAxis !== undefined ||
    ctx.planeNormalReversed !== undefined ||
    ctx.topAttachment !== undefined ||
    ctx.bottomAttachment !== undefined
  );
}

/** Transport→model CONTEXT_DATA header (+ INLINE MTextData / BlockData) —
 * shared by the PRIMARY context and each SECONDARY block. The transport's
 * DxfPoint3D ({x,y,z}) satisfies drawingPoint3DSchema, so the spread feeds Zod
 * directly; the nested INLINE blocks are parsed by their own schema.
 *
 * ABSENCE-PRESERVING (#294 r3): `mleaderContextSchema` carries NO
 * `.default(...)`s — a transport field the source block omitted stays absent
 * on the model. Before r3 the primary schema defaulted scale/hasMText/
 * hasBlock/planeNormalReversed, so a SPARSE primary (e.g. only an explicit
 * `290 0`) materialized `scale: 1` / `hasBlock: false` /
 * `planeNormalReversed: false` here and the export invented `40 1` / `296 0`
 * / `297 0` groups (the r2 fix covered only SECONDARY headers via a separate
 * no-defaults schema — now unified). */
function dxfMLeaderContextToModel(
  ctx: DxfMLeaderContext,
): NonNullable<MultileaderEntity["contextData"]> {
  return mleaderContextSchema.parse({
    ...ctx,
    mTextData: ctx.mTextData
      ? mleaderMTextDataSchema.parse({ ...ctx.mTextData })
      : undefined,
    blockData: ctx.blockData
      ? mleaderBlockDataSchema.parse({ ...ctx.blockData })
      : undefined,
  });
}

/** Transport→model leader hierarchy. Carries the sub-slice-A per-LEADER (node)
 * and per-LEADER_LINE (line) typed fields — incl. the break index (group 90)
 * and break-point z that the prior 2D mapper dropped. The transport's 3D node
 * points + breaks spread straight onto the (optional-z) model shape; 3D vertices
 * collapse to the model's 2D point. */
function dxfMLeaderLeaderRootsToModel(
  roots: DxfMLeaderEntity["geometry"]["leaderRoots"],
): MultileaderEntity["leaderRoots"] {
  return roots.map((root) => ({
    nodes: root.nodes.map((node) => {
      const { lines, ...nodeRest } = node;
      return {
        ...nodeRest,
        lines: lines.map((line) => {
          const { vertices, breakPoints, arrowheadBlock, ...lineRest } = line;
          return {
            ...lineRest,
            // FIX 3 (sub-slice D follow-up) — preserve leader-line vertex z
            // (group 30). This slice upgraded the sibling breakPoints /
            // lastLeaderPoint / doglegVector to 3D; vertices were still mapped
            // 2D (toDrawingPoint) and silently dropped z. The transport carries
            // a full DxfPoint3D and the writer already emits 10/20/30, so map
            // through toDrawingPoint3D onto the model's now optional-z vertices.
            vertices: vertices.map((v) => toDrawingPoint3D(v)),
            breakPoints: breakPoints ?? [],
            arrowheadBlock: arrowheadBlock ?? "",
          };
        }),
      };
    }),
  }));
}

// The heavy-POLYLINE child-record projectors (VERTEX run + terminating
// SEQEND) live in their own leaf, `./dxfBridge.polylineChildRecords` — this
// file is at the CLAUDE.md 2000-line ceiling it was itself split out to
// respect. IMPORTED (this module calls both, and a bare re-export would not
// bind them here) and RE-EXPORTED, so `dxfBridge.ts`'s `export *` keeps the
// public surface unchanged.
export {
  toPolylineSeqendMetadata,
  toPolylineVertexRecord,
} from "./dxfBridge.polylineChildRecords";

// Return type is the widened union DrawingEntity | AttdefEntity because
// ATTDEF can legally appear as a block child (BlockChildEntity); the
// discriminated-union DrawingEntity does not include ATTDEF at the top
// level so callers that push into a DrawingSpace must filter it out.
export function toDrawingEntity(
  entity: DxfSupportedEntity,
  entityId: string,
  spaceId: string
): DrawingEntity | AttdefEntity {
  const arrayFields = parseArrayXData(entity);

  const base = {
    id: entityId,
    spaceId,
    layer: entity.common.layer ?? "0",
    // EPIC-COLOR-UNION S1 — preserve the full DXF colour (group 62 + 420 +
    // 430) as the typed union instead of dropping 420/430 (true-colour /
    // colour-book were lost before this slice). dxf-io already parses all
    // three group codes onto `common`; `colorFromDxf` folds them per the
    // 420→430→62 precedence (Autodesk DXF ref / ezdxf).
    color: colorFromDxf(entity.common),
    linetype: entity.common.linetype ?? "ByLayer",
    lineweight: entity.common.lineweight !== undefined ? String(entity.common.lineweight) : "ByLayer",
    transparency: entity.common.transparency !== undefined ? String(entity.common.transparency) : "0",
    // Per-entity linetype scale (DXF group 48 → common.linetypeScale). Set
    // only when the DXF carried a value (absent ⇒ undefined ⇒ read as 1
    // downstream). Threaded into the PDF dash emitter (PDF-6.1).
    ...(entity.common.linetypeScale !== undefined
      ? { linetypeScale: entity.common.linetypeScale }
      : {}),
    ...(arrayFields.arrayGroupId ? { arrayGroupId: arrayFields.arrayGroupId } : {}),
    ...(arrayFields.arraySourceOfGroup ? { arraySourceOfGroup: arrayFields.arraySourceOfGroup } : {}),
    ...(arrayFields.arrayMode ? { arrayMode: arrayFields.arrayMode } : {}),
    // DXF common reference handles + flags (S-COMMON-HANDLES). Copy from
    // the parsed common props so they survive onto the typed model and
    // can be re-emitted on export. Symmetric to the export mapping in
    // dxfBridge.entityConversion.ts (toDxfEntity).
    ...(entity.common.ownerHandle ? { ownerHandle: entity.common.ownerHandle } : {}),
    ...(entity.common.materialHandle ? { materialHandle: entity.common.materialHandle } : {}),
    ...(entity.common.plotStyleHandle ? { plotStyleHandle: entity.common.plotStyleHandle } : {}),
    // Entity extension-dictionary 360 handle (Batch-8 — needed for the
    // ARRAY → AcDbAssocArray join + ext-dict round-trip).
    ...(entity.common.extensionDictionaryHandle
      ? { extensionDictionaryHandle: entity.common.extensionDictionaryHandle }
      : {}),
    ...(entity.common.visibility !== undefined ? { visibility: entity.common.visibility } : {}),
    ...(entity.common.shadowMode !== undefined ? { shadowMode: entity.common.shadowMode } : {}),
    // Entity-level unknown / vendor DXF group passthrough (S-COMMON-XDATA).
    // Copy the raw groups the entity parser captured (DxfGroup[]) onto the
    // typed model — structurally identical to the model's local {code,value}
    // shape, so a plain array copy suffices. Symmetric to the export mapping
    // in dxfBridge.entityConversion.ts (toDxfEntity). Set only when non-empty
    // so entities without unknown groups read as `undefined`.
    //
    // MLEADER EXCLUSION: the entityParser decodes the CONTEXT_DATA / LEADER /
    // LEADER_LINE marker groups into geometry.leaderRoots WHILE ALSO leaving
    // them in draft.extraGroups (they have no MLEADER group-reader). Carrying
    // them onto the typed model would re-emit them on export ON TOP of the
    // leaderRoots-derived CONTEXT_DATA block the writer rebuilds — a duplicate
    // / corrupt MLEADER. MLEADER's full structure round-trips via leaderRoots,
    // so we drop its extraGroups on this leg too. This is the ONLY type with
    // the decoded-and-also-in-extraGroups overlap (every other finalizer pushes
    // to extraGroups only on the not-consumed passthrough path). Symmetric to
    // the export-side exclusion in dxfBridge.entityConversion.ts (toDxfEntity).
    ...(entity.type !== "MLEADER" &&
    entity.extraGroups &&
    entity.extraGroups.length > 0
      ? { extraGroups: entity.extraGroups }
      : {})
  };

  if (entity.type === "LINE") {
    const e: LineEntity = {
      ...base,
      type: "Line",
      // Preserve 3D endpoints (DXF group 30/31) across round-trip (DEFUSE S1b).
      start: toDrawingPoint3D(entity.geometry.start),
      end: toDrawingPoint3D(entity.geometry.end),
      thickness: entity.common.thickness ?? 0,
      // DXF group 210/220/230 OCS normal (S-EXTRUSION-STORE). Only carry a
      // non-default extrusion to the model — absent ⇒ the (0,0,1) default.
      ...extrusionField(entity.common.extrusion)
    };
    return e;
  }

  if (entity.type === "POINT") {
    const e: PointEntity = {
      ...base,
      type: "Point",
      // Preserve 3D location (DXF group 30) across round-trip (DEFUSE S1b).
      location: toDrawingPoint3D(entity.geometry.location),
      rotation: entity.geometry.rotation ?? 0,
      thickness: entity.common.thickness ?? 0,
      // DXF group 210/220/230 OCS normal (S-EXTRUSION-STORE).
      ...extrusionField(entity.common.extrusion)
    };
    return e;
  }

  if (entity.type === "CIRCLE") {
    const e: CircleEntity = {
      ...base,
      type: "Circle",
      // Preserve 3D center (DXF group 30) across round-trip (DEFUSE S-ARC-CIRCLE-CENTER-Z).
      center: toDrawingPoint3D(entity.geometry.center),
      radius: entity.geometry.radius,
      thickness: entity.common.thickness ?? 0,
      // DXF group 210/220/230 OCS normal (S-EXTRUSION-STORE).
      ...extrusionField(entity.common.extrusion)
    };
    return e;
  }

  if (entity.type === "ARC") {
    const e: ArcEntity = {
      ...base,
      type: "Arc",
      // Preserve 3D center (DXF group 30) across round-trip (DEFUSE S-ARC-CIRCLE-CENTER-Z).
      center: toDrawingPoint3D(entity.geometry.center),
      radius: entity.geometry.radius,
      startAngle: entity.geometry.startAngle,
      endAngle: entity.geometry.endAngle,
      thickness: entity.common.thickness ?? 0,
      // DXF group 210/220/230 OCS normal (S-EXTRUSION-STORE).
      ...extrusionField(entity.common.extrusion)
    };
    return e;
  }

  if (entity.type === "ELLIPSE") {
    return dxfEllipseToDrawingEllipse(entity, base);
  }

  if (entity.type === "SPLINE") {
    return dxfSplineToDrawingSpline(entity, base);
  }

  if (entity.type === "LWPOLYLINE") {
    const g = entity.geometry;
    const e: LwpolylineEntity = {
      ...base,
      type: "LWPOLYLINE",
      vertices: g.vertices.map(toDrawingVertex),
      closed: g.closed,
      constantWidth: g.constantWidth ?? 0,
      elevation: g.elevation ?? 0,
      thickness: entity.common.thickness ?? 0,
      // DXF group 210/220/230 OCS normal (S-EXTRUSION-STORE).
      ...extrusionField(entity.common.extrusion)
    };
    // DXF group 70 bit 128 (S-LWPOLYLINE) — only stored when set so the
    // typed model stays compact (absent ⇒ false).
    if (g.plinegen) e.plinegen = true;
    return e;
  }

  if (entity.type === "TEXT") {
    const g = entity.geometry;
    const result: TextEntity = {
      ...base,
      type: "Text",
      insertionPoint: toDrawingPoint(g.insertionPoint),
      height: g.height,
      rotation: g.rotation ?? 0,
      contents: g.contents,
      styleName: g.styleName ?? "Standard",
      widthFactor: g.widthFactor ?? 1,
      obliqueAngle: g.obliqueAngle ?? 0,
      hJustification: g.hJustification ?? 0,
      vJustification: g.vJustification ?? 0,
      thickness: entity.common.thickness ?? 0,
      generationFlags: g.generationFlags ?? 0,
      annotativeContexts: [],
      extrusion: entity.common.extrusion
        ? toDrawingPoint3D(entity.common.extrusion)
        : { x: 0, y: 0, z: 1 }
    };
    if (g.alignmentPoint) result.alignmentPoint = toDrawingPoint(g.alignmentPoint);
    return result;
  }

  if (entity.type === "MTEXT") {
    const g = entity.geometry;
    const e: MtextEntity = {
      ...base,
      type: "MText",
      insertionPoint: toDrawingPoint(g.insertionPoint),
      nominalHeight: g.nominalHeight,
      referenceWidth: g.referenceWidth ?? g.nominalHeight * 10,
      attachmentPoint: g.attachmentPoint ?? 1,
      drawingDirection: g.drawingDirection ?? 1,
      contents: g.contents,
      styleName: g.styleName ?? "Standard",
      rotation: g.rotation ?? 0,
      lineSpacingStyle: g.lineSpacingStyle ?? 1,
      lineSpacingFactor: g.lineSpacingFactor ?? 1,
      backgroundFill: (g.backgroundFillSetting ?? 0) > 0,
      columnType: g.columnType ?? 0,
      columnCount: g.columnCount ?? 1,
      xAxisDirection: g.xAxisDirection ? toDrawingPoint3D(g.xAxisDirection) : { x: 1, y: 0, z: 0 },
      // Codex #342 F3 — OCS handedness for a mirrored MTEXT (group 210/220/230).
      // The parser puts the extrusion on `common.extrusion`; `extrusionField`
      // carries only a NON-default value onto the model (absent/identity ⇒ no
      // field), matching how Text reads its extrusion above.
      ...extrusionField(entity.common.extrusion),
      backgroundFillSetting: g.backgroundFillSetting ?? 0,
      fillBoxScale: g.fillBoxScale ?? 1.5,
      backgroundFillColorACI: g.backgroundFillColorACI ?? 0,
      backgroundTransparency: g.backgroundTransparency ?? 0,
      columnFlowReversed: g.columnFlowReversed ?? false,
      columnAutoheight: g.columnAutoheight ?? false,
      columnWidth: g.columnWidth ?? 0,
      columnGutter: g.columnGutter ?? 0,
      columnHeights: g.columnHeights ? [...g.columnHeights] : [],
      horizontalWidth: g.horizontalWidth ?? 0,
      verticalHeight: g.verticalHeight ?? 0,
      annotativeContexts: []
    };
    if (g.backgroundFillColorRGB !== undefined) e.backgroundFillColorRGB = g.backgroundFillColorRGB;
    return e;
  }

  if (entity.type === "DIMENSION") {
    const g = entity.geometry;
    const result: DimensionEntity = {
      ...base,
      type: "Dimension",
      // SL-3 P2 — strip ONLY the block-exclusive bit 32 (a serialization flag,
      // "always set in R13+"), not a model-semantic subtype. It is deterministically
      // re-added at the export boundary (`toDxfEntity` ORs DIM_BLOCK_EXCLUSIVE), so
      // the model carries the LOGICAL group-70 (subtype 0-6 + genuinely-semantic
      // bit 64 ordinate-X / bit 128 user-text-location) and round-trips byte-stable
      // (file 32 → model 0 → export 32). Using `& ~32` (not `& 7`) PRESERVES bits
      // 64/128. Without this, a from-scratch linear/aligned dim saved & reopened
      // parses back as dimensionType 32/33, breaking raw `=== 0 || === 1` consumers
      // (DIMBASELINE/DIMCONTINUE pre-arm in useDraftSession).
      dimensionType: g.dimensionType & ~DIM_BLOCK_EXCLUSIVE,
      defPoint: toDrawingPoint(g.defPoint),
      textMidpoint: toDrawingPoint(g.textMidpoint),
      dimensionText: g.dimensionText ?? "",
      styleName: g.styleName ?? "Standard",
      textHeight: 0,
      textRotation: g.textRotation ?? 0,
      linearAngle: g.linearAngle ?? 0,
      measurement: g.measurement ?? 0,
      // Group 71 — attachment point. Parsed value when present; AutoCAD
      // default 5 (Middle-Center) otherwise.
      attachmentPoint: g.attachmentPoint ?? 5,
      obliqueAngle: g.obliqueAngle ?? 0,
      textAngleRelativeToDimLine: g.textAngleRelativeToDimLine ?? 0,
      leaderLength: g.leaderLength ?? 0,
      anonymousBlockName: g.anonymousBlockName ?? "",
      // Group C — per-scale annotative contexts. The Dxf parser does
      // not yet decode the ACAD_ANNOTATIVE XData block on DIMENSION
      // entities; this field is populated via the style's DIMANNO flag
      // + post-parse enrichment. Empty default preserves round-trip.
      annotativeContexts: [],
      // Opt 2b — per-entity DIMVAR overrides parsed from ACAD/DSTYLE
      // XData (group codes 1000+). Only fields present in the XData
      // are populated; missing fields stay `undefined` so consumers can
      // distinguish "use named style" from "explicit override".
      styleOverrides: extractDimensionStyleOverrides(entity.xdata),
      // Issue #565 — MIRRTEXT-driven glyph handedness, read from this app's
      // OWN EDIT_DWG_DIM_OVERRIDES XDATA marker (see
      // `dimensionTextMirroredXData.ts`'s module doc for the full
      // rationale — AcDbDimension has no native DXF group for this).
      // Omitted entirely (not an explicit `false`) when absent, matching
      // every other `.optional()` field's convention in this object
      // literal — `readDimensionTextMirrored` already returns `false` for
      // "no marker", so the spread simply contributes nothing in that case.
      ...(readDimensionTextMirrored(entity.xdata) ? { textMirrored: true } : {}),
      // S-DIMENSION-EXTRAS — extrusion(210/220/230) rides the COMMON channel
      // like Line/Circle/Arc (DIMENSION was simply absent from
      // S-EXTRUSION-STORE / S-COMMON-EXPORT). Only carry a non-default
      // extrusion onto the model (absent ⇒ the (0,0,1) default).
      ...extrusionField(entity.common.extrusion),
    };
    if (g.extLine1Point) result.extLine1Point = toDrawingPoint(g.extLine1Point);
    if (g.extLine2Point) result.extLine2Point = toDrawingPoint(g.extLine2Point);
    if (g.thirdPoint) result.thirdPoint = toDrawingPoint3D(g.thirdPoint);
    if (g.arcPoint) result.arcPoint = toDrawingPoint3D(g.arcPoint);
    // S-DIMENSION-EXTRAS — thickness(39, COMMON channel) + version(280),
    // lineSpacingStyle(72)/Factor(41), clone insertion point (12/22/32).
    // Carried only when the DXF supplied a non-default value so default
    // dimensions stay compact on a re-export (thickness is additive-optional).
    if (entity.common.thickness !== undefined && entity.common.thickness !== 0) {
      result.thickness = entity.common.thickness;
    }
    if (g.version !== undefined) result.version = g.version;
    if (g.lineSpacingStyle !== undefined) result.lineSpacingStyle = g.lineSpacingStyle;
    if (g.lineSpacingFactor !== undefined) result.lineSpacingFactor = g.lineSpacingFactor;
    if (g.insertionPoint) result.insertionPoint = toDrawingPoint3D(g.insertionPoint);
    return result;
  }

  if (entity.type === "INSERT") {
    const g = entity.geometry;
    // Materialise any ATTRIB children that were attached to the INSERT
    // during parse. Each ATTRIB maps to a BlockInsertAttribute; the
    // override fields only fill in when the ATTRIB value diverges from
    // an as-authored ATTDEF (handled downstream in Phase 1.6/1.7 —
    // Phase 1 tasks 1.1-1.4 only need round-trip fidelity here).
    const attributes = (g.attribs ?? []).map(
      (attrib): BlockInsertAttribute => {
        const a: BlockInsertAttribute = {
          tag: attrib.geometry.tag,
          value: attrib.geometry.value,
          handle: attrib.common.handle ?? "",
        };
        // Capture per-ATTRIB rotation/height/position overrides so
        // they can be used by the renderer / property palette once
        // it starts consuming them (Phase 1.5+). Safe to always
        // populate — downstream code either reads or ignores them.
        if (attrib.geometry.rotation !== undefined) {
          a.overrideRotation = attrib.geometry.rotation;
        }
        if (attrib.geometry.height !== undefined) {
          a.overrideHeight = attrib.geometry.height;
        }
        // GitHub Codex review PR #558 (post-rebase fresh pass) P2 — DXF
        // group 71 (text generation flags), the raw ATTRIB's OWN handedness
        // bits. The parser already reads this into `attrib.geometry.
        // generationFlags` (`ATTDEF_GROUP_READERS`'s shared 71 reader,
        // `entityParser.ts`) — it was simply never captured onto the typed
        // `BlockInsertAttribute` here (`model.ts`'s `overrideGenerationFlags`
        // doc comment). Captured verbatim like rotation/height above.
        if (attrib.geometry.generationFlags !== undefined) {
          a.overrideGenerationFlags = attrib.geometry.generationFlags;
        }
        a.overridePosition = toDrawingPoint(attrib.geometry.insertionPoint);
        if (attrib.geometry.styleName) {
          a.overrideStyle = attrib.geometry.styleName;
        }
        if (attrib.geometry.flagBits.invisible) a.invisible = true;
        return a;
      }
    );
    const e: BlockInsertEntity = {
      ...base,
      type: "Block",
      blockName: g.blockName,
      // Preserve 3D insertion point (DXF group 30) across round-trip (DEFUSE S-INSERT-INSERTPT-Z).
      insertionPoint: toDrawingPoint3D(g.insertionPoint),
      scaleX: g.scaleX ?? 1,
      scaleY: g.scaleY ?? 1,
      scaleZ: g.scaleZ ?? 1,
      rotation: g.rotation ?? 0,
      columnCount: g.columnCount ?? 1,
      rowCount: g.rowCount ?? 1,
      columnSpacing: g.columnSpacing ?? 0,
      rowSpacing: g.rowSpacing ?? 0,
      extrusion: g.extrusion
        ? toDrawingPoint3D(g.extrusion)
        : { x: 0, y: 0, z: 1 },
      attributes,
      customProperties: {},
      annotationScales: [],
      annotativeContexts: [],
    };
    // Phase N.4 — propagate per-INSERT representation data linkage
    // captured by the parser into the typed BlockReference schema. The
    // parameter override map and visibility-state id are populated by
    // the OBJECTS-section walker downstream once the dictionary chain
    // is resolved; the linkage handles + etag round-trip even when the
    // walker cannot identify the parameter map (defensive fidelity).
    if (g.representationData) {
      const overrides: NonNullable<BlockInsertEntity["representationOverrides"]> = {
        parameterOverrides: g.representationData.parameterOverrides ?? {},
      };
      if (g.representationData.currentVisibilityStateId !== undefined) {
        overrides.currentVisibilityStateId =
          g.representationData.currentVisibilityStateId;
      }
      if (g.representationData.etag !== undefined) {
        overrides.representationEtag = g.representationData.etag;
      }
      if (g.representationData.dataHandle !== undefined) {
        overrides.representationDataHandle =
          g.representationData.dataHandle;
      }
      e.representationOverrides = overrides;
    }
    // T9b review round 1 (P1) — the parser attaches the terminating SEQEND's
    // own group-5 to the INSERT (`geometry.seqendHandle`); carry it onto the
    // typed `BlockInsertEntity.seqendHandle` so an IMPORTED document (which
    // bypasses the from-scratch skeleton remint) re-emits `SEQEND 5 <h>` —
    // dropping it here re-exported a handle-less SEQEND that ezdxf silently
    // re-minted and AutoCAD recovered.
    if (g.seqendHandle !== undefined) e.seqendHandle = g.seqendHandle;
    return e;
  }

  if (entity.type === "HATCH") {
    const g = entity.geometry;
    const e: HatchEntity = {
      ...base,
      type: "Hatch",
      patternName: g.patternName,
      solidFill: g.solidFill,
      associative: g.associative,
      hatchStyle: g.hatchStyle ?? 0,
      patternType: g.patternType ?? 1,
      patternAngle: g.patternAngle ?? 0,
      patternScale: g.patternScale ?? 1,
      // S-HATCH-CUSTOM — pattern double-line flag (group 77). Was hardcoded
      // `false` (the dxf-io geometry carried no doubleLine); now projected from
      // the typed DXF geometry so a double-line custom pattern round-trips.
      doubleLine: g.doubleLine ?? false,
      boundaryPaths: g.boundaryPaths.map((p: DxfHatchBoundaryPath): HatchBoundaryPath => ({
        pathTypeFlag: p.pathType,
        edges: p.edges.map(toDrawingHatchEdge),
        // S-HATCH-ASSOC-REFS — per-path associative source-boundary handles.
        ...(p.sourceBoundaryHandles && p.sourceBoundaryHandles.length > 0
          ? { sourceBoundaryHandles: [...p.sourceBoundaryHandles] }
          : {})
      })),
      elevation: g.elevation ?? 0,
      seedPoints: g.seedPoints?.map(sp => ({ x: sp.x, y: sp.y })) ?? [],
      annotativeContexts: [],
      // S-HATCH-EXTRUSION — project the typed OCS normal (group 210/220/230)
      // captured by the dxf-io parser onto the typed model. Omitted (absent
      // field) for the identity (0,0,1) normal so default hatches stay compact.
      ...extrusionField(g.extrusion)
    };
    // S-HATCH-ELEVATION-XY — leading 10/20 of the WCS elevation point.
    if (g.elevationX !== undefined) e.elevationX = g.elevationX;
    if (g.elevationY !== undefined) e.elevationY = g.elevationY;
    // S-HATCH-PIXELSIZE — DXF group 47.
    if (g.pixelSize !== undefined) e.pixelSize = g.pixelSize;
    // S-HATCH-CUSTOM — custom pattern-definition lines (78 + 53/43/44/45/46/
    // 79/49). Structured per-line (offset is {x,y}, NOT flattened). Deep-copied
    // so the typed model never aliases the DXF geometry array.
    if (g.customPatternLines && g.customPatternLines.length > 0) {
      e.customPatternLines = g.customPatternLines.map((line) => ({
        angle: line.angle,
        base: { x: line.base.x, y: line.base.y },
        offset: { x: line.offset.x, y: line.offset.y },
        dashLengths: [...line.dashLengths],
      }));
    }
    // Gradient fill (PDF-6.5a) — pass-through structural map. Present only
    // for gradient hatches; absent leaves `gradient` undefined so solid/
    // pattern hatches round-trip byte-identically.
    if (g.gradient) {
      e.gradient = {
        kind: g.gradient.kind,
        reserved: g.gradient.reserved,
        oneColor: g.gradient.oneColor,
        numberOfColors: g.gradient.numberOfColors,
        rotation: g.gradient.rotation,
        shift: g.gradient.shift,
        tint: g.gradient.tint,
        name: g.gradient.name,
        colors: g.gradient.colors.map(c => ({
          position: c.position,
          ...(c.aci !== undefined ? { aci: c.aci } : {}),
          ...(c.trueColor !== undefined ? { trueColor: c.trueColor } : {})
        }))
      };
    }
    return e;
  }

  if (entity.type === "VIEWPORT") {
    const g = entity.geometry;
    // P1 #2 fix: round-trip group-90 statusBitFlags so the lock bit (0x4000)
    // and other viewport flags survive DXF export/import.
    // displayLocked is derived from the statusBitFlags bit 0x4000 (already done
    // in the entityParser); we preserve the full bitfield here.
    const displayLockedFromBits = g.displayLocked ?? false;
    const e: ViewportEntity = {
      ...base,
      type: "Viewport",
      center: toDrawingPoint(g.center),
      width: g.width,
      height: g.height,
      viewCenter: g.viewCenter ? { x: g.viewCenter.x, y: g.viewCenter.y } : { x: 0, y: 0 },
      viewHeight: g.viewHeight ?? 100,
      viewDirection: g.viewDirection ? toDrawingPoint3D(g.viewDirection) : { x: 0, y: 0, z: 1 },
      viewTarget: g.viewTarget ? toDrawingPoint3D(g.viewTarget) : { x: 0, y: 0, z: 0 },
      viewTwistAngle: g.viewTwistAngle ?? 0,
      lensLength: g.lensLength ?? 50,
      frontClipZ: g.frontClipZ ?? 0,
      backClipZ: g.backClipZ ?? 0,
      viewportStatus: g.viewportStatus ?? 1,
      displayLocked: displayLockedFromBits,
      frozenLayers: [],
      ...(g.viewportId !== undefined ? { viewportId: g.viewportId } : {}),
      ...(g.statusBitFlags !== undefined ? { statusBitFlags: g.statusBitFlags } : {}),
    };
    // S-VIEWPORT-A — lighting block (DXF 141 brightness / 142 contrast).
    // Truth-corrected: 142 was previously surfaced as
    // `viewportCannoscaleFactor` (PR-13 mis-map — per the Autodesk DXF
    // reference + ezdxf acdb_viewport, 142 is view CONTRAST and 141 view
    // BRIGHTNESS; the real per-VP annotation scale rides the ext-dict
    // XRECORD ASDK_XREC_ANNOTATION_SCALE_INFO → 340 → AcDbScale, preserved
    // via the OBJECTS passthrough). `viewportCannoscaleName/Factor` are no
    // longer populated from any entity-level group — they remain in-session
    // fields (viewport-scale commands); the deferred ext-dict resolver is
    // the documented follow-up (model.ts viewportEntitySchema comment).
    if (g.brightness !== undefined) e.brightness = g.brightness;
    if (g.contrast !== undefined) e.contrast = g.contrast;
    // Batch-5 S-VIEWPORT-SNAPGRID — surface typed snap/grid scalars (DXF
    // 13/23, 14/24, 15/25, 50, 61, 72) onto the model. Set only when present
    // so untouched viewports read these as undefined (round-trip-identical).
    if (g.snapBase) e.snapBase = toDrawingPoint(g.snapBase);
    if (g.snapSpacing) e.snapSpacing = toDrawingPoint(g.snapSpacing);
    if (g.gridSpacing) e.gridSpacing = toDrawingPoint(g.gridSpacing);
    if (g.snapRotation !== undefined) e.snapRotation = g.snapRotation;
    if (g.gridMajor !== undefined) e.gridMajor = g.gridMajor;
    if (g.circleZoomPercent !== undefined) e.circleZoomPercent = g.circleZoomPercent;
    // Batch-5 S-VIEWPORT-SHADEPLOT — plot-only shade/render mode (DXF 170/281).
    if (g.shadePlotMode !== undefined) e.shadePlotMode = g.shadePlotMode;
    if (g.renderMode !== undefined) e.renderMode = g.renderMode;
    // S-VIEWPORT-OVERRIDE-331 — carry the native per-VP frozen-layer handles
    // (331) + property-override soft-pointers (335/343/344/91) onto the model
    // so they survive a model round-trip as TYPED fields (the S-COMMON-XDATA
    // base spread above carries viewport extraGroups too, but typed handles
    // keep the writer's single-emit drop-set discipline). Handle-preservation
    // only — distinct from the EDIT_DWG_VP_OVERRIDES XDATA frozenLayers names.
    if (g.frozenLayerHandles && g.frozenLayerHandles.length > 0) {
      e.frozenLayerHandles = [...g.frozenLayerHandles];
    }
    if (g.layerOverrideHandles && g.layerOverrideHandles.length > 0) {
      e.layerOverrideHandles = g.layerOverrideHandles.map((r) => ({
        code: r.code,
        handle: r.handle,
      }));
    }
    // S-VIEWPORT-CLIP-HANDLE — carry the native clipping-boundary handle (340)
    // onto the model so a clipped viewport survives a model round-trip as a
    // TYPED field. Set only when present (preserve-source absence). Handle-
    // preservation only — resolving it to clip geometry is the deferred slices
    // 2/3.
    if (g.clippingBoundaryHandle !== undefined) {
      e.clippingBoundaryHandle = g.clippingBoundaryHandle;
    }
    // P1 #1 / iter-6 fix: read EDIT_DWG_VP_OVERRIDES XDATA and copy the FULL
    // ViewportOverrides schema into the in-memory entity. Previously only
    // frozenLayers + displayLocked were copied, silently dropping scale
    // (num/denom) and ucsName — so those fields were lost on the next export.
    // Now all four fields are stored explicitly so writeViewportOverrides on
    // re-export emits the authoritative XDATA values rather than re-deriving
    // an approximation from viewHeight/height floats.
    if (entity.xdata && entity.xdata.length > 0) {
      const overrides = readViewportOverrides({
        displayLocked: e.displayLocked,
        frozenLayers: e.frozenLayers,
        viewHeight: e.viewHeight,
        height: e.height,
        xdata: entity.xdata as { appName: string; groups: { code: number; value: string }[] }[],
      });
      e.frozenLayers = overrides.frozenLayers;
      e.displayLocked = overrides.locked;
      e.vpOverrideScale = overrides.scale;
      if (overrides.ucsName !== undefined) {
        e.vpOverrideUcsName = overrides.ucsName;
      }
      // @codex #514 P2 "Don't trust native 331 for unclassified XDATA viewports"
      // — preserve the writer-version marker itself on the entity so the import
      // reconciliation pass can positively identify a v3+ trustworthy-native
      // viewport from its OWN decoded XDATA, without depending on the raw-
      // document handle-keyed pre-scan (`collectXdataAuthoritativeVpHandles`),
      // which cannot classify a viewport whose native `handle` is absent.
      if (overrides.writerVersion !== undefined) {
        e.vpOverridesWriterVersion = overrides.writerVersion;
      }
      // S-VIEWPORT-A — legacy PR-13 own-export recovery (evidence-based; NOT
      // value-shape inference). Window: PR-13 (252db54b, 2026-05-02) →
      // S-VIEWPORT-A, when OUR writer emitted `142 ← viewportCannoscaleFactor`
      // into the slot that per the DXF spec is view CONTRAST. The only
      // in-session producer of that factor (applyViewportSetScale, since MC5
      // iter-2 eaeb7682) computed factor = scaleNum / scaleDenom AND stamped
      // the SAME rational pair into the EDIT_DWG_VP_OVERRIDES XDATA, which the
      // bridge export (MC5 iter-4 cd99e638) wrote on EVERY viewport — so each
      // own-export whose 142 carries an app-authored factor also carries our
      // marker with the identical division.
      //
      // FINGERPRINT (own marker — all three parts required):
      //   (1) OUR appid block carries EXPLICIT scale codes (scaleFromXdata; a
      //       viewHeight/height-derived ratio can never trigger this), AND
      //   (2) the incoming 142 equals num/denom within 1e-9 relative — covers
      //       the writer's toFixed(12) text round-trip of the same double, AND
      //   (3) OUR block carries NO writer-version marker (#304 r2:
      //       `1071 vpOverridesWriterVersion` — stamped by every v2+ export,
      //       absent from every legacy-window file; the current export path
      //       writes scale codes on EVERY viewport, so without this gate a
      //       fresh export with genuine contrast == num/denom — e.g. contrast
      //       0.5 at scale 1:2 — would lose its contrast on the next import).
      // On match the 142 is OUR mis-written factor: route it back to the
      // cannoscale channel (exactly the channel the legacy importer populated)
      // and do NOT project contrast.
      //
      // Why DROP the 142 on re-export instead of preserving it: the on-disk
      // 142 was spec-corrupt at old-export time — it never was a contrast
      // value, so re-emitting it as contrast would persist the corruption into
      // a now correctly-typed slot. Nothing is lost: the authoritative scale
      // still rides the XDATA (vpOverrideScale → writeViewportOverrides) and
      // the restored in-session factor. A real-AutoCAD file (no EDIT_DWG
      // XDATA) or a non-matching value (e.g. genuine contrast on a file we
      // once exported and AutoCAD re-saved) never fingerprints — contrast
      // wins, matching what a real AutoCAD reader does with those bytes.
      // Residual ambiguity is now confined to the pre-marker post-correction
      // window (S-VIEWPORT-A → #304 r2): a genuine contrast EXACTLY equal to
      // the XDATA ratio in a file from that window still fingerprints. That
      // remainder is theoretical (real contrast defaults to 0.0, the ratio is
      // always > 0) and is accepted and documented here rather than guessed
      // around; every marker-bearing export is exact.
      const xdataScale = overrides.scaleFromXdata;
      if (
        e.contrast !== undefined &&
        overrides.writerVersion === undefined &&
        xdataScale !== undefined &&
        xdataScale.num > 0 &&
        xdataScale.denom > 0
      ) {
        const ratio = xdataScale.num / xdataScale.denom;
        if (Math.abs(e.contrast - ratio) <= 1e-9 * Math.max(1, ratio)) {
          e.viewportCannoscaleFactor = e.contrast;
          delete e.contrast;
        }
      }
    }
    return e;
  }

  if (entity.type === "MLEADER") {
    const g = entity.geometry;
    // S-MLEADER-CONTEXT-STRUCTURAL (sub-slice D) — project the typed CONTEXT_DATA
    // record + the verbatim in-context remainder onto the model whenever the
    // context is SUBSTANTIVE: it carries ANY typed field (isSubstantiveMLeader
    // Context) OR a non-empty rawContextGroups (genuinely-unrecognised groups
    // must survive verbatim — ПРИОРИТЕТ #1). Only a pure marker-only synthetic
    // (ZERO typed fields AND empty rawContextGroups — the #243 degenerate case)
    // is rejected; it keeps the writer's fallback round-trip, whose group-340
    // stamp the faithful path now also emits, so the in-context text-style handle
    // survives via `textStyleHandle` either way.
    const hasRawContextGroups =
      g.rawContextGroups !== undefined && g.rawContextGroups.length > 0;
    const contextData =
      g.contextData &&
      (isSubstantiveMLeaderContext(g.contextData) || hasRawContextGroups)
        ? dxfMLeaderContextToModel(g.contextData)
        : undefined;
    const rawContextGroups =
      contextData && g.rawContextGroups && g.rawContextGroups.length > 0
        ? g.rawContextGroups.map((grp) => ({ code: grp.code, value: grp.value }))
        : undefined;
    // #294 Codex P2 — annotative SECONDARY CONTEXT_DATA blocks. The transport
    // keeps every block after the first fully typed (context + leaderRoots +
    // per-block raw remainder), but the bridge dropped them: a direct dxf-io
    // parse→serialize round-tripped while the app path (importDxfStringTo
    // DrawingDocument → exportDrawingDocumentToDxfString) lost the blocks —
    // and, after #285-9 moved secondary raw groups OFF the flat primary
    // channel, lost secondary-only raw groups entirely. Project each block
    // TYPED through the same context / leader-root converters as the primary
    // (one converter since #294 r3 unified the no-defaults header schema);
    // absence-preserving (no secondary blocks → field absent on the model).
    const secondaryContexts =
      g.secondaryContexts && g.secondaryContexts.length > 0
        ? g.secondaryContexts.map((sec) => ({
            context: dxfMLeaderContextToModel(sec.context),
            leaderRoots: dxfMLeaderLeaderRootsToModel(sec.leaderRoots),
            ...(sec.rawContextGroups && sec.rawContextGroups.length > 0
              ? {
                  rawContextGroups: sec.rawContextGroups.map((grp) => ({
                    code: grp.code,
                    value: grp.value,
                  })),
                }
              : {}),
          }))
        : undefined;
    const e: MultileaderEntity = {
      ...base,
      type: "Multileader",
      styleName: g.styleName ?? "Standard",
      // SL-5 P2 — carry the source body-340 MLEADERSTYLE handle verbatim so a
      // re-export re-emits the same 340 (source-preserve). SL-5 P3a — carry the
      // source body-270 version. Absence-preserving (set only when present).
      ...(g.leaderStyleHandle !== undefined
        ? { leaderStyleHandle: g.leaderStyleHandle }
        : {}),
      ...(g.version !== undefined ? { version: g.version } : {}),
      // Full leader hierarchy incl. the sub-slice-A per-LEADER / per-LEADER_LINE
      // typed fields + break indices (sub-slice D — projected through the bridge).
      leaderRoots: dxfMLeaderLeaderRootsToModel(g.leaderRoots),
      ...(contextData ? { contextData } : {}),
      ...(rawContextGroups ? { rawContextGroups } : {}),
      ...(secondaryContexts ? { secondaryContexts } : {}),
      contentType: g.contentType,
      textContents: g.textContents ?? "",
      // Group 340 (MTextData.style_handle) resolved to a STYLE name by
      // the parser pass-2 resolver; fall back to "Standard" when the DXF
      // omitted the handle or it could not be resolved.
      textStyleName: g.textStyleName ?? "Standard",
      // The MLEADER text height's authoritative DXF home in a context-bearing
      // MLEADER is CONTEXT_DATA group 41 (charHeight) — the byte-faithful writer
      // SUPPRESSES body 40 whenever charHeight is present. Hydrate the model's
      // entity field from the typed context first (mirroring textRotation below)
      // so it reflects the active height AND so a no-edit export's divergence gate
      // (modelMLeaderContextToDxf) sees textHeight === charHeight → no spurious
      // re-projection. Fall back to the body-40 channel, then the default (Codex
      // #270 textHeight projection arm).
      textHeight: g.contextData?.charHeight ?? g.textHeight ?? 2.5,
      // textRotation lives in MTextData group 42 (its real ezdxf home — body 42
      // is arrow_head_size), so hydrate the model's body field from the typed
      // context so it reflects reality + round-trips out via the same channel.
      textRotation: g.contextData?.mTextData?.rotation ?? g.textRotation ?? 0,
      // GitHub Codex review PR #558 P2 — same hydration pattern as
      // `textRotation` immediately above, for the MTextData OCS extrusion
      // (group 11/21/31) handedness companion. No flat dxf-io transport
      // twin exists for this (unlike `g.textRotation`'s no-context fallback
      // channel) — `textMirrored` is a model-layer-only convenience field,
      // so a source with no CONTEXT_DATA MTextData reads `false` (default,
      // matching a plain (0,0,1) extrusion).
      textMirrored: isMLeaderMTextMirroredExtrusion(g.contextData?.mTextData?.extrusion),
      // gap-D (DEFERRED, tracked — audit wa4pzsuwp): the MLEADER text colour's
      // real DXF home is CONTEXT_DATA MTextData.color (group 90), which
      // round-trips verbatim via `contextData.mTextData.color`. This entity-level
      // `textColor` is a UI/property convenience field with no body group, so the
      // hardcoded "ByLayer" default is lossless for round-trip (the source colour
      // survives on contextData). Promoting it to the UI is a deferred render arm.
      textColor: "ByLayer",
      textAttachmentType: g.textAttachmentType ?? 1,
      textAttachmentDirection: g.textAttachmentDirection ?? 0,
      textFrameDisplay: g.textFrameDisplay ?? false,
      // Line spacing's authoritative DXF home in a context-bearing MLEADER is
      // CONTEXT_DATA MTextData group 45 (lineSpacingFactor) / 170
      // (lineSpacingStyle) — its real ezdxf home (body 48/49 were removed in
      // SL-5 P3c). Hydrate the model's flat body fields from the typed context
      // first (mirroring textRotation / charHeight above) so a round-tripped
      // MLEADER reflects the real spacing AND an imported real-AutoCAD MLEADER's
      // 45/170 projects to the flat field the renderer reads. Fall back to the
      // (model-authored fallback) flat channel, then the default 1.
      textLineSpacingStyle:
        g.contextData?.mTextData?.lineSpacingStyle ?? g.textLineSpacingStyle ?? 1,
      textLineSpacingFactor:
        g.contextData?.mTextData?.lineSpacingFactor ?? g.textLineSpacingFactor ?? 1,
      textLineSpacingDistance: g.textLineSpacingDistance ?? 0,
      textWidth: g.textWidth ?? 0,
      arrowheadSize: g.arrowheadSize ?? 2.5,
      arrowheadBlock: g.arrowheadBlock ?? "",
      leaderLineType: g.leaderLineType ?? 1,
      leaderLineColor: g.leaderLineColor ?? "ByBlock",
      // #258 F3 — the transport carries the raw DXF group-171 enum (string form,
      // e.g. "-2"/"25"); map it back to the model lineweight string
      // ("ByBlock"/"ByLayer"/"0.25"). Absent → ByBlock default. Symmetric to the
      // export-side modelLeaderLineWeightToDxf171 in dxfBridge.entityConversion.
      // #258 F3 — the transport carries the raw DXF group-171 enum (string form,
      // e.g. "-2"/"25"); map it back to the model lineweight string
      // ("ByBlock"/"ByLayer"/"0.25"). Absent → ByBlock default. Symmetric to the
      // export-side modelLeaderLineWeightToDxf171 in dxfBridge.entityConversion.
      leaderLineWeight: dxf171ToModelLeaderLineWeight(g.leaderLineWeight),
      landingEnabled: g.landingEnabled ?? true,
      doglegEnabled: g.doglegEnabled ?? true,
      doglegLength: g.doglegLength ?? 8,
      // The landing gap's DXF home is CONTEXT_DATA 145 (landingGapSize), NOT a
      // body code (gap-C.2 removed body 44). Derive from the typed context first
      // so a context-bearing MLEADER reflects its real gap; fall back to the
      // (model-authored fallback) body channel, then the default 2 (Codex #271).
      landingGap: g.contextData?.landingGapSize ?? g.landingGap ?? 2,
      breakSize: g.breakSize ?? 3.75,
      blockName: g.blockName ?? "",
      // Raw body-344 BLOCK_RECORD handle carried verbatim so a re-export re-emits
      // the same 344; blockName above is the pass-2-resolved human-readable name
      // (Codex #271).
      ...(g.blockRecordHandle !== undefined
        ? { blockRecordHandle: g.blockRecordHandle }
        : {}),
      blockScale: g.blockScale ?? 1,
      blockRotation: g.blockRotation ?? 0,
      // #432 r11 (@codex seam) — carry the body block_scale_vector (10/20/30) onto
      // the model, source-preserving (only when the transport had it) so the app
      // save path re-emits it. Symmetric to the export projection in
      // dxfBridge.entityConversion.
      ...(g.blockScaleVector !== undefined
        ? {
            blockScaleVector: {
              x: g.blockScaleVector.x,
              y: g.blockScaleVector.y,
              z: g.blockScaleVector.z,
            },
          }
        : {}),
      // gap-D (DEFERRED, tracked — audit wa4pzsuwp): the block content colour's
      // real DXF home is CONTEXT_DATA BlockData.color (group 93), round-tripped
      // verbatim via `contextData.blockData.color`. This entity-level `blockColor`
      // has no body group, so the hardcoded "ByBlock" default is lossless for
      // round-trip (the source colour survives on contextData). UI arm deferred.
      blockColor: "ByBlock",
      // AcDbMLeader BODY group 176 (block_connection_type) — preserve verbatim,
      // INCLUDING source absence (Codex #270 regression fix). A `?? 0`
      // materialized 0 on a source that omitted body 176, and the byte-faithful
      // writer then re-emitted an invented `176\n0` every round-trip. Carry the
      // value only when the transport actually had it; an omitted 176 stays
      // omitted (model field is now `.optional()`, export guards `!== undefined`).
      ...(g.blockConnectionType !== undefined
        ? { blockConnectionType: g.blockConnectionType }
        : {}),
      blockAttributeReferences: (g.blockAttributeReferences ?? []).map(ref => ({
        attDefHandle: ref.attDefHandle,
        index: ref.index,
        width: ref.width,
        value: ref.value,
      })),
      annotativeContexts: (g.annotativeContexts ?? []).map(ctx => ({
        scale: ctx.scale,
        // Human-readable scale name added as part of Group C to share
        // the common annotativeContextSchema; MLEADER parsers that
        // haven't surfaced a scale name fall back to empty string.
        scaleName: "",
        textHeight: ctx.textHeight ?? 2.5,
        arrowheadSize: ctx.arrowheadSize ?? 2.5,
        doglegLength: ctx.doglegLength ?? 8,
        landingGap: ctx.landingGap ?? 2,
        textRotation: ctx.textRotation ?? 0,
        textColor: ctx.textColor ?? "ByLayer",
        textWidth: ctx.textWidth ?? 0,
      })),
      textLocation: g.textLocation ? toDrawingPoint(g.textLocation) : { x: 0, y: 0 },
      ...(g.basePoint ? { basePoint: toDrawingPoint(g.basePoint) } : {}),
      propertyOverrideFlags: g.propertyOverrideFlags ?? 0,
      // gap-C.2 — AcDbMLeader BODY group 175 (text_alignment_type). Carry the
      // source value verbatim (was previously conflated with body 90 override
      // flags); default 0 when the DXF omitted it.
      textAlignmentType: g.textAlignmentType ?? 0,
      // PR-14 — entity-level "is annotative" is derived from
      // annotativeContexts.length > 0; no longer a stored bool.
      extraMlVars: g.extraMlVars ?? {},
    };
    return e;
  }

  if (entity.type === "REVCLOUD") {
    const g = entity.geometry;
    const e: RevisionCloudEntity = {
      ...base,
      type: "Revision Cloud",
      vertices: g.vertices.map(toDrawingVertex),
      closed: g.closed,
      // DXF group 70 bit 128 — plinegen. Carried through alongside closed (bit
      // 1) so it round-trips like an LWPOLYLINE; only set when true (compact 70).
      ...(g.plinegen ? { plinegen: true } : {}),
      arcChordLength: g.arcLength,
      // RevcloudProps XData (decoded by the parser): approximate arc
      // length (1040), style (1070), creation mode (1070). Fall back to
      // the LWPOLYLINE-derived arcLength / AutoCAD defaults when absent.
      approximateArcLength: g.approximateArcLength ?? g.arcLength,
      revisionCloudStyle: g.revisionCloudStyle ?? "Normal",
      creationMode: isRevcloudCreationMode(g.creationMode)
        ? g.creationMode
        : "Rectangular",
      // S-REVCLOUD-FIELDS — LWPOLYLINE-derived scalars the bridge previously
      // dropped: constantWidth (43) + elevation (38) live on the geometry;
      // thickness (39) flows through the COMMON channel like Line/Circle/Arc.
      constantWidth: g.constantWidth ?? 0,
      elevation: g.elevation ?? 0,
      thickness: entity.common.thickness ?? 0,
      // DXF group 210/220/230 OCS normal via the COMMON channel
      // (S-EXTRUSION-STORE). Only carry a non-default extrusion onto the model.
      ...extrusionField(entity.common.extrusion),
    };
    return e;
  }

  if (entity.type === "WIPEOUT") {
    const g = entity.geometry;
    const e: WipeoutEntity = {
      ...base,
      type: "Wipeout",
      vertices: g.vertices.map((v) => ({ x: v.x, y: v.y })),
      frame: g.frame,
      clipMode: g.clipMode
    };
    return e;
  }

  if (entity.type === "ATTDEF") {
    const g = entity.geometry;
    const e: AttdefEntity = {
      ...base,
      type: "Attdef",
      handle: entity.common.handle ?? "",
      tag: g.tag,
      prompt: g.prompt,
      defaultValue: g.defaultValue,
      insertionPoint: toDrawingPoint(g.insertionPoint),
      height: g.height,
      rotation: g.rotation ?? 0,
      styleName: g.styleName ?? "Standard",
      widthFactor: g.widthFactor ?? 1,
      obliqueAngle: g.obliqueAngle ?? 0,
      generationFlags: g.generationFlags ?? 0,
      hJustification: g.hJustification ?? 0,
      vJustification: g.vJustification ?? 0,
      flags: {
        invisible: g.flagBits.invisible,
        constant: g.flagBits.constant,
        verify: g.flagBits.verify,
        preset: g.flagBits.preset,
        lockPosition: g.flagBits.lockPosition,
        multiline: g.flagBits.multiline,
      },
      backgroundFill: g.backgroundFill !== undefined && g.backgroundFill !== 0,
      fillBoxScale: g.fillBoxScale ?? 1.5,
      annotativeContexts: [],
      // DXF group 210/220/230 OCS normal (S-EXTRUSION-STORE).
      ...extrusionField(entity.common.extrusion),
    };
    if (g.alignmentPoint) e.alignmentPoint = toDrawingPoint(g.alignmentPoint);
    return e;
  }

  if (entity.type === "ATTRIB" || entity.type === "SEQEND") {
    // Orphan ATTRIB / stand-alone SEQEND: the parser normally attaches
    // them to the owning INSERT. If we still see one here it's a
    // malformed DXF. We project it to a minimal Text entity so the
    // drawing surface doesn't crash and a user can see the orphan
    // tag/value on screen.
    const g = entity.type === "ATTRIB" ? entity.geometry : null;
    const fallback: TextEntity = {
      ...base,
      type: "Text",
      insertionPoint: g ? toDrawingPoint(g.insertionPoint) : { x: 0, y: 0 },
      height: g?.height ?? 2.5,
      rotation: g?.rotation ?? 0,
      contents: g?.value ?? "",
      styleName: g?.styleName ?? "Standard",
      widthFactor: g?.widthFactor ?? 1,
      obliqueAngle: g?.obliqueAngle ?? 0,
      hJustification: g?.hJustification ?? 0,
      vJustification: g?.vJustification ?? 0,
      thickness: 0,
      generationFlags: g?.generationFlags ?? 0,
      extrusion: { x: 0, y: 0, z: 1 },
      annotativeContexts: [],
    };
    return fallback;
  }

  if (entity.type === "POLYLINE") {
    // Legacy heavy POLYLINE. The VERTEX children were already folded onto the
    // entity by the shared grouping pass (dxf-io `entityGrouping`), so they
    // arrive here as `geometry.vertices`. Layer / linetype / lineweight /
    // colour are not independently editable on a VERTEX in AutoCAD (ezdxf
    // `Polyline.on_layer_change` rewrites every child), so the export
    // re-derives those from this parent; everything that belongs to the
    // VERTEX alone rides `toPolylineVertexRecord` — see
    // `polylineVertexRecordSchema`.
    const g = entity.geometry;
    const e: PolylineEntity = {
      ...base,
      type: "Polyline",
      flags: g.flags,
      elevation: toDrawingPoint3D(g.elevation),
      vertices: g.vertices.map(toPolylineVertexRecord),
      thickness: entity.common.thickness ?? 0,
      ...extrusionField(entity.common.extrusion),
    };
    if (g.defaultStartWidth !== undefined) e.defaultStartWidth = g.defaultStartWidth;
    if (g.defaultEndWidth !== undefined) e.defaultEndWidth = g.defaultEndWidth;
    if (g.meshMCount !== undefined) e.meshMCount = g.meshMCount;
    if (g.meshNCount !== undefined) e.meshNCount = g.meshNCount;
    if (g.smoothSurfaceMDensity !== undefined) {
      e.smoothSurfaceMDensity = g.smoothSurfaceMDensity;
    }
    if (g.smoothSurfaceNDensity !== undefined) {
      e.smoothSurfaceNDensity = g.smoothSurfaceNDensity;
    }
    if (g.surfaceType !== undefined) e.surfaceType = g.surfaceType;
    if (g.verticesFollow !== undefined) e.verticesFollow = g.verticesFollow;
    if (g.seqend) {
      if (g.seqend.common.handle) e.seqendHandle = g.seqend.common.handle;
      // The rest of the terminator — its own layer, XDATA, extension
      // dictionary, `102 {ACAD_REACTORS`, unmapped groups and a non-canonical
      // subclass extension. The dxf-io grouping pass deliberately retains the
      // WHOLE parsed SEQEND (`entityGrouping.ts`: "POLYLINE keeps the WHOLE
      // SEQEND, unlike INSERT"), and keeping only its handle here threw all of
      // that away — the export leg then rebuilt the record from this parent
      // with empty `subclassMarkers` / `extraGroups`, so an ordinary
      // open-and-save DELETED it (PR #785 @codex round 2, F1).
      const seqendMetadata = toPolylineSeqendMetadata(g.seqend);
      if (seqendMetadata !== undefined) e.seqendMetadata = seqendMetadata;
    }
    return e;
  }

  if (entity.type === "VERTEX") {
    // An ORPHAN VERTEX — the grouping pass folds every VERTEX onto its owning
    // POLYLINE, so reaching here means a malformed file with a VERTEX that has
    // no preceding POLYLINE. Project it to a single-vertex Polyline rather
    // than dropping it, mirroring the orphan-ATTRIB → Text projection above:
    // nothing is silently lost, and the record stays visible and selectable.
    const orphanVertex = toPolylineVertexRecord(entity);
    const e: PolylineEntity = {
      ...base,
      type: "Polyline",
      flags: 0,
      elevation: { x: 0, y: 0, z: 0 },
      vertices: [orphanVertex],
      thickness: entity.common.thickness ?? 0,
      ...extrusionField(entity.common.extrusion),
    };
    return e;
  }

  if (entity.type === "ACAD_TABLE") {
    const g = entity.geometry;
    const e: TableEntity = {
      ...base,
      type: "Table",
      blockName: g.blockName,
      insertionPoint: toDrawingPoint3D(g.insertionPoint),
      rowHeights: [...g.rowHeights],
      columnWidths: [...g.columnWidths],
      // Verbatim AcDbTable body — the single write source for export. The
      // rowHeights / columnWidths above are a READ-ONLY projection over these
      // same groups; see `tableEntitySchema`'s doc.
      contentGroups: g.contentGroups.map((group) => ({
        code: group.code,
        value: group.value,
      })),
      cachedGraphicsChunks: [...g.cachedGraphicsChunks],
    };
    if (g.horizontalDirection) {
      e.horizontalDirection = toDrawingPoint3D(g.horizontalDirection);
    }
    if (g.version !== undefined) e.version = g.version;
    if (g.tableStyleHandle !== undefined) e.tableStyleHandle = g.tableStyleHandle;
    if (g.blockRecordHandle !== undefined) {
      e.blockRecordHandle = g.blockRecordHandle;
    }
    if (g.valueFlag !== undefined) e.valueFlag = g.valueFlag;
    if (g.rowCount !== undefined) e.rowCount = g.rowCount;
    if (g.columnCount !== undefined) e.columnCount = g.columnCount;
    if (g.overrideFlag !== undefined) e.overrideFlag = g.overrideFlag;
    if (g.borderColorOverrideFlag !== undefined) {
      e.borderColorOverrideFlag = g.borderColorOverrideFlag;
    }
    if (g.borderLineweightOverrideFlag !== undefined) {
      e.borderLineweightOverrideFlag = g.borderLineweightOverrideFlag;
    }
    if (g.borderVisibilityOverrideFlag !== undefined) {
      e.borderVisibilityOverrideFlag = g.borderVisibilityOverrideFlag;
    }
    if (g.cachedGraphicsByteCount !== undefined) {
      e.cachedGraphicsByteCount = g.cachedGraphicsByteCount;
    }
    return e;
  }

  if (entity.type === "OLE2FRAME") {
    const g = entity.geometry;
    const e: OleFrameEntity = {
      ...base,
      type: "OLE Frame",
      upperLeft: toDrawingPoint3D(g.upperLeft),
      lowerRight: toDrawingPoint3D(g.lowerRight),
      // The OLE compound document, verbatim. NEVER decoded — see
      // `oleFrameEntitySchema`'s doc for why the embedded EMF raster is not
      // rendered and the entity draws as its frame instead.
      binaryChunks: [...g.binaryChunks],
    };
    if (g.oleVersion !== undefined) e.oleVersion = g.oleVersion;
    if (g.objectDescription !== undefined) {
      e.objectDescription = g.objectDescription;
    }
    if (g.oleObjectType !== undefined) e.oleObjectType = g.oleObjectType;
    if (g.tileModeDescriptor !== undefined) {
      e.tileModeDescriptor = g.tileModeDescriptor;
    }
    if (g.drawAspect !== undefined) e.drawAspect = g.drawAspect;
    if (g.binaryByteCount !== undefined) e.binaryByteCount = g.binaryByteCount;
    if (g.terminator !== undefined) e.terminator = g.terminator;
    return e;
  }

  // ARRAYRECT — the INSERT-grid scalars are read from the entity's own group
  // codes here. The associative payload (associative flag, sourceEntityIds,
  // the §9.4 typed params + itemTransforms) is NOT known at this point — it
  // lives in the AcDbAssocArray OBJECTS graph and is applied in a SECOND PASS
  // by `applyAssocArrayResolution` (dxfBridge.import.ts) using the resolver
  // output, exactly like annotativeContexts. We therefore default
  // `associative:false` (a bare ARRAYRECT block-ref with no assoc graph IS
  // non-associative — the INSERT-grid fallback) and let the second pass flip
  // it to true + attach the typed graph when a graph is found. Batch-8
  // S-ARRAYASSOC-MODEL — no longer hardcodes associative:true/sourceEntityIds.
  const g = entity.geometry;
  const e: ArrayRectEntity = {
    ...base,
    type: "Rectangular Associative Array",
    // S-ARRAYRECT-BASEPOINT-Z — carry the insertion point Z (group 30) onto the
    // model verbatim instead of collapsing to 2D (the prior `toDrawingPoint`
    // silently dropped a non-zero base-point Z).
    basePoint: toDrawingPoint3D(g.insertionPoint),
    rowCount: g.rowCount,
    columnCount: g.columnCount,
    // The dxf-io spacing is presence-aware (`undefined` = group 44/45 ABSENT
    // in the source — Codex W2 #251-B follow-up). The model field is a
    // required number, so absence collapses to 0 HERE; the assoc-array second
    // pass receives the PRE-collapse dxf-io optionality directly (see
    // `applyAssocArrayResolution` in dxfBridge.import.ts) so an explicit
    // source `44 0` stays distinguishable from an absent group.
    rowSpacing: g.rowSpacing ?? 0,
    columnSpacing: g.columnSpacing ?? 0,
    rowTotal: g.rowCount * (g.rowSpacing ?? 0),
    columnTotal: g.columnCount * (g.columnSpacing ?? 0),
    axisAngle: g.rotation ?? 0,
    associative: false,
    sourceEntityIds: [],
  };
  // SL-18 — preserve the source block-reference name (DXF group 2) so a
  // NON-associative grid can re-emit as a genuine MINSERT (`0 INSERT` +
  // AcDbMInsertBlock) referencing its real BLOCK_RECORD. Set only when the
  // source carried a non-empty group 2 (absence-preserving); the second-pass
  // assoc resolver may still flip `associative` true, in which case the export
  // keeps a plain block reference and this field is simply unused.
  if (g.blockName) e.sourceBlockName = g.blockName;
  return e;
}

/* ── Per-entity DIMVAR override XData decoder ──────────── */

/**
 * Decode the per-entity DIMSTYLE overrides stored in `ACAD` XData on a
 * DIMENSION entity. AutoCAD emits these as the `DSTYLE` block:
 *
 *   1001 ACAD            (xdata appName captured in DxfXDataEntry.appName)
 *   1000 DSTYLE          (block marker)
 *   1002 {               (open paren)
 *   1070 <dimvar-code>   (e.g. 41 for DIMASZ)
 *   <value-code> <value> (1040 real / 1070 int / 1000 string)
 *   ... (repeating)
 *   1002 }               (close paren)
 *
 * Returns an object containing only the overrides actually present; keys
 * not present in the XData stay `undefined` so consumers can distinguish
 * "use the style" from "use this explicit value". Unknown DIMVAR codes
 * (e.g. DIMSCALE, DIMTAD — out of scope for Phase 1b's 23-var set) are
 * silently skipped.
 *
 * Tolerant of malformed input — a missing trailing value group, an
 * unmatched `{` / `}`, or a non-ACAD xdata entry simply yields an empty
 * map.
 */
export function extractDimensionStyleOverrides(
  xdata: DxfXDataEntry[] | undefined,
): DimensionStyleOverrides {
  // Keyed by string (not just DimVarKey) so the SL-11 arrowhead PROVENANCE twin
  // fields (`dimblk*Handle`) can be populated alongside the DIMVAR keys.
  const out: Record<string, number | string | boolean> = {};
  if (!xdata || xdata.length === 0) return out as DimensionStyleOverrides;

  for (const entry of xdata) {
    if (entry.appName !== "ACAD") continue;
    const groups = entry.groups;
    // Find DSTYLE block markers.
    let i = 0;
    while (i < groups.length) {
      const g = groups[i];
      if (g.code === 1000 && g.value === "DSTYLE") {
        // Next group should be 1002 "{" — skip if present.
        i += 1;
        if (i < groups.length && groups[i].code === 1002 && groups[i].value === "{") {
          i += 1;
        }
        // Walk (1070 dimvar-code) (value-group) pairs until 1002 "}".
        while (i < groups.length) {
          const head = groups[i];
          if (head.code === 1002 && head.value === "}") {
            i += 1;
            break;
          }
          if (head.code !== 1070) {
            i += 1;
            continue;
          }
          const dimVarCode = Number.parseInt(head.value, 10);
          const def = DIM_VAR_BY_GROUP_CODE.get(dimVarCode);
          const value = groups[i + 1];
          if (!def || !value) {
            i += 2;
            continue;
          }
          if (def.blockRef && value.code === 1005) {
            // SL-11 #3/#4 — an arrowhead override that arrived as a `1005`
            // soft-pointer HANDLE is IMPORTED-source provenance: preserve it in
            // the parallel `*Handle` twin (verbatim — preserve-source-never-infer)
            // so the export treats it as a handle (kept/remapped, NEVER resolved
            // as a same-spelled block name). An R12-shaped `1000 <name>` value
            // (rare) falls through to the name branch below (authored provenance).
            out[`${def.key}Handle`] = value.value;
          } else if (def.type === "string") {
            out[def.key] = value.value;
          } else if (def.type === "boolean") {
            // XData encodes booleans as integers (1070).
            const n = Number.parseInt(value.value, 10);
            out[def.key] = n !== 0;
          } else if (def.type === "int") {
            out[def.key] = Number.parseInt(value.value, 10);
          } else {
            // "number" — float (XData code 1040 typically).
            out[def.key] = Number.parseFloat(value.value);
          }
          i += 2;
        }
        continue;
      }
      i += 1;
    }
  }

  return out as DimensionStyleOverrides;
}

/* ── Style Table Conversion: DXF → DrawingModel ──────────── */

export function dxfTextStyleToDrawingTextStyle(entry: DxfTextStyleEntry): TextStyle {
  return {
    name: entry.name,
    fontName: entry.fontName ?? "txt",
    fontFile: "",
    bigFontFile: entry.bigFontFile ?? "",
    height: entry.height ?? 0,
    widthFactor: entry.widthFactor ?? 1,
    obliqueAngle: entry.obliqueAngle ?? 0,
    flags: entry.flags ?? 0,
    lastHeight: entry.lastHeight ?? 2.5,
    generationFlags: entry.generationFlags ?? 0,
    fontCharset: entry.fontCharset ?? 0,
    // ROOT E — carry the subclass-marker presence so export re-emits the 100/
    // groups ONLY for sources that had them (R12 stays byte-faithful).
    ...(entry.hadSubclassMarker !== undefined
      ? { hadSubclassMarker: entry.hadSubclassMarker }
      : {}),
    // T9b — carry the STYLE record's own group-5 + `330` owner verbatim so a
    // real AutoCAD file re-exports the SAME record handle / owner.
    ...(entry.handle !== undefined ? { handle: entry.handle } : {}),
    ...(entry.ownerHandle !== undefined
      ? { ownerHandle: entry.ownerHandle }
      : {}),
  };
}

export function dxfDimStyleToDrawingDimStyle(entry: DxfDimensionStyleEntry): DimensionStyle {
  const dv = entry.dimVars;

  // Helpers: pull typed values from the parsed dimVars map with fallback.
  const num = (key: string, fallback: number): number =>
    typeof dv[key] === "number" ? (dv[key] as number) : fallback;
  const str = (key: string, fallback: string): string =>
    typeof dv[key] === "string" ? (dv[key] as string) : fallback;
  // DXF encodes booleans as integer 0/1 — convert with fallback.
  const bool = (key: string, fallback: boolean): boolean => {
    const v = dv[key];
    if (typeof v === "number") return v !== 0;
    return fallback;
  };

  // Fields we explicitly map into the DimensionStyle shape. Everything else
  // in dv gets stashed into extraDimVars for lossless round-trip.
  const knownFields = new Set([
    // Text
    "DIMTXT", "DIMTXSTY", "DIMCLRT", "DIMTIH", "DIMTOH", "DIMTAD", "DIMJUST",
    "DIMGAP", "DIMTVP", "DIMTSZ",
    // Arrows
    "DIMASZ", "DIMBLK", "DIMBLK1", "DIMBLK2", "DIMLDRBLK", "DIMSAH",
    // Lines
    "DIMCLRD", "DIMCLRE", "DIMLWD", "DIMLWE",
    "DIMEXO", "DIMEXE", "DIMDLI", "DIMDLE",
    "DIMSE1", "DIMSE2", "DIMSD1", "DIMSD2",
    // Primary units
    "DIMSCALE", "DIMLFAC", "DIMDEC", "DIMRND", "DIMLUNIT", "DIMPOST", "DIMAPOST",
    "DIMDSEP", "DIMFRAC",
    // Angular units
    "DIMAUNIT", "DIMADEC",
    // Tolerances
    "DIMTOL", "DIMLIM", "DIMTP", "DIMTM", "DIMTFAC", "DIMTOLJ", "DIMTDEC",
    // Fit / placement
    "DIMATFIT", "DIMTOFL", "DIMSOXD", "DIMTIX", "DIMTMOVE", "DIMUPT",
    // Center marks
    "DIMCEN",
    // Zero suppression
    "DIMZIN", "DIMTZIN", "DIMAZIN",
    // Alternate units
    "DIMALT", "DIMALTD", "DIMALTF", "DIMALTU", "DIMALTRND", "DIMALTTD",
    "DIMALTZ", "DIMALTTZ",
    // Annotative (Group C)
    "DIMANNO",
  ]);
  const extraDimVars: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(dv)) {
    if (!knownFields.has(key)) {
      extraDimVars[key] = value;
    }
  }

  return {
    name: entry.name,
    // SL-4 — carry the DIMSTYLE record's own group-105 handle so import → model
    // → re-export preserves it (re-emitted at 105). Omitted when the source had
    // no record handle (a handle-less / from-scratch DIMSTYLE).
    ...(entry.handle !== undefined ? { handle: entry.handle } : {}),
    // T9b — carry the record's `330` owner (DIMSTYLE table head) verbatim.
    ...(entry.ownerHandle !== undefined
      ? { ownerHandle: entry.ownerHandle }
      : {}),
    // Text
    DIMTXT: num("DIMTXT", 2.5),
    DIMTXSTY: str("DIMTXSTY", "Standard"),
    DIMCLRT: num("DIMCLRT", 0),
    DIMTIH: bool("DIMTIH", true),
    DIMTOH: bool("DIMTOH", true),
    DIMTAD: num("DIMTAD", 0),
    DIMJUST: num("DIMJUST", 0),
    DIMGAP: num("DIMGAP", 0.625),
    DIMTVP: num("DIMTVP", 0),
    DIMTSZ: num("DIMTSZ", 0),
    // Arrows
    DIMASZ: num("DIMASZ", 2.5),
    DIMBLK: str("DIMBLK", ""),
    DIMBLK1: str("DIMBLK1", ""),
    DIMBLK2: str("DIMBLK2", ""),
    DIMLDRBLK: str("DIMLDRBLK", ""),
    DIMSAH: bool("DIMSAH", false),
    // Lines & extension lines
    DIMCLRD: num("DIMCLRD", 0),
    DIMCLRE: num("DIMCLRE", 0),
    DIMLWD: num("DIMLWD", -2),
    DIMLWE: num("DIMLWE", -2),
    DIMEXO: num("DIMEXO", 0.625),
    DIMEXE: num("DIMEXE", 1.25),
    DIMDLI: num("DIMDLI", 3.75),
    DIMDLE: num("DIMDLE", 0),
    DIMSE1: bool("DIMSE1", false),
    DIMSE2: bool("DIMSE2", false),
    DIMSD1: bool("DIMSD1", false),
    DIMSD2: bool("DIMSD2", false),
    // Primary units
    DIMSCALE: num("DIMSCALE", 1),
    DIMLFAC: num("DIMLFAC", 1),
    DIMDEC: num("DIMDEC", 4),
    DIMRND: num("DIMRND", 0),
    DIMLUNIT: num("DIMLUNIT", 2),
    DIMPOST: str("DIMPOST", ""),
    DIMAPOST: str("DIMAPOST", ""),
    DIMDSEP: num("DIMDSEP", 46),
    DIMFRAC: num("DIMFRAC", 0),
    // Angular units
    DIMAUNIT: num("DIMAUNIT", 0),
    DIMADEC: num("DIMADEC", 0),
    // Tolerances
    DIMTOL: bool("DIMTOL", false),
    DIMLIM: bool("DIMLIM", false),
    DIMTP: num("DIMTP", 0),
    DIMTM: num("DIMTM", 0),
    DIMTFAC: num("DIMTFAC", 1),
    DIMTOLJ: num("DIMTOLJ", 1),
    DIMTDEC: num("DIMTDEC", 4),
    // Fit / placement
    DIMATFIT: num("DIMATFIT", 3),
    DIMTOFL: bool("DIMTOFL", false),
    DIMSOXD: bool("DIMSOXD", false),
    DIMTIX: bool("DIMTIX", false),
    DIMTMOVE: num("DIMTMOVE", 0),
    DIMUPT: bool("DIMUPT", false),
    // Center marks
    DIMCEN: num("DIMCEN", 2.5),
    // Zero suppression
    DIMZIN: num("DIMZIN", 0),
    DIMTZIN: num("DIMTZIN", 0),
    DIMAZIN: num("DIMAZIN", 0),
    // Alternate units
    DIMALT: bool("DIMALT", false),
    DIMALTD: num("DIMALTD", 2),
    DIMALTF: num("DIMALTF", 25.4),
    DIMALTU: num("DIMALTU", 2),
    DIMALTRND: num("DIMALTRND", 0),
    DIMALTTD: num("DIMALTTD", 2),
    DIMALTZ: num("DIMALTZ", 0),
    DIMALTTZ: num("DIMALTTZ", 0),
    // Annotative (Group C) — populated by parser from DIMSTYLE flag
    // byte bit 8; entry-level fallback here reads the normalized
    // DIMANNO dimvar if the parser already decomposed the flag.
    DIMANNO: num("DIMANNO", 0),
    extraDimVars,
    // ROOT E — subclass-marker presence (see dxfTextStyleToDrawingTextStyle).
    ...(entry.hadSubclassMarker !== undefined
      ? { hadSubclassMarker: entry.hadSubclassMarker }
      : {}),
  };
}

// Build a populated "Standard" DimensionStyle by parsing through the schema
// — this guarantees all DIMVAR defaults are filled in (including new fields
// added later) without manual maintenance.
export function buildDefaultStandardDimensionStyle(): DimensionStyle {
  return dimensionStyleSchema.parse({ name: "Standard" });
}

export function buildDefaultStandardTextStyle(): TextStyle {
  return textStyleSchema.parse({ name: "Standard", fontName: "txt" });
}

export function buildDefaultStandardMultileaderStyle(): MultileaderStyle {
  return multileaderStyleSchema.parse({ name: "Standard" });
}

/**
 * Convert a fully-parsed DXF MLEADERSTYLE entry into the DrawingModel
 * MultileaderStyle. Handle references that the parser was able to
 * resolve (textStyleName, leaderLineType, arrowheadBlock,
 * blockContentName) come through as plain names; unresolved handles
 * fall back to schema defaults. Unknown group codes round-trip via
 * extraMlVars.
 */
export function dxfMLeaderStyleToDrawingMLeaderStyle(
  dxfStyle: DxfMultileaderStyleEntry,
): MultileaderStyle {
  // Convert the captured extraGroups into the string-only record the
  // schema expects.
  const extraMlVars: Record<string, string> = {};
  for (const [key, value] of Object.entries(dxfStyle.extraGroups)) {
    extraMlVars[key] = value;
  }

  return multileaderStyleSchema.parse({
    name: dxfStyle.name || "Standard",
    contentType: dxfStyle.contentType ?? 2,
    textStyleName: dxfStyle.textStyleName ?? "Standard",
    textHeight: dxfStyle.textHeight ?? 2.5,
    textColor: dxfStyle.textColor ?? "ByBlock",
    textAttachmentType: dxfStyle.textLeftAttachmentType ?? 1,
    // SL-5 F1 (@codex round-6) — carry group 178 (text_right_attachment_type)
    // onto the model so the app import→export path preserves it (was dropped: no
    // model home). Set only when the source carried 178 — the field is OPTIONAL,
    // so a source without 178 stays absent (never re-emitted as a fabricated
    // default). Mirrors `sourceHandle` (present-only) spread below.
    ...(dxfStyle.textRightAttachmentType !== undefined
      ? { textRightAttachmentType: dxfStyle.textRightAttachmentType }
      : {}),
    textAttachmentDirection: dxfStyle.textAttachmentDirection ?? 0,
    textLineSpacingStyle: 1,
    textLineSpacingFactor: 1,
    textLineSpacingDistance: 0,
    textFrameDisplay: dxfStyle.textFrameEnabled ?? false,
    textWidth: 0,
    leaderType: dxfStyle.leaderType ?? 1,
    leaderLineType: dxfStyle.leaderLineType ?? "ByBlock",
    leaderColor: dxfStyle.leaderLineColor ?? "ByBlock",
    leaderWeight: dxfStyle.leaderLineWeight ?? "ByBlock",
    landingEnabled: dxfStyle.enableLanding ?? true,
    doglegEnabled: dxfStyle.enableDogleg ?? true,
    // SL-5 P3b — CORRECTED: dogleg_length is code 43 (dxfStyle.doglegLength),
    // landing_gap_size is code 42 (dxfStyle.landingDistance), align_space is code
    // 46 (dxfStyle.alignSpace). The prior mapping conflated doglegLength ← 42 and
    // landingGap ← 46 while dropping 43 entirely (non-default dogleg lost).
    doglegLength: dxfStyle.doglegLength ?? 8,
    landingGap: dxfStyle.landingDistance ?? 2,
    alignSpace: dxfStyle.alignSpace ?? 4,
    maxLeaderSegments: dxfStyle.maxLeaderSegmentPointsNumber ?? 2,
    arrowheadSize: dxfStyle.arrowheadSize ?? 2.5,
    arrowheadBlock: dxfStyle.arrowheadBlock ?? "",
    blockName: dxfStyle.blockContentName ?? "",
    blockScale: dxfStyle.blockContentScaleX ?? 1,
    blockColor: dxfStyle.blockContentColor ?? "ByBlock",
    blockConnectionType: dxfStyle.blockContentConnectionType ?? 0,
    blockRotation: dxfStyle.blockContentRotation ?? 0,
    enableAnnotation: dxfStyle.enableAnnotationScale ?? false,
    overallScale: dxfStyle.scaleOverall ?? 1,
    breakEnabled: true,
    breakGapSize: dxfStyle.breakGapSize ?? 3.75,
    breakSize: 3.75,
    // SL-5 F6 — carry the source MLEADERSTYLE object handle (group 5) so the
    // export can REUSE it (byte-stable object handle + dict child across
    // save→reopen→save). Omitted for a marker-less / handle-less source so a
    // genuinely-authored style mints fresh on export.
    ...(dxfStyle.handle ? { sourceHandle: dxfStyle.handle } : {}),
    extraMlVars,
  });
}

// Extract a known subset of ATTDEF group codes from the raw group list
// of an unsupported-entity record. Used during block definition import
// as a fallback when the DXF contains ATTDEF as an unsupported-entity
// (older parsers). Modern parses hit `rawEntity.type === "ATTDEF"` via
// the supported-entity branch and use the full parseSupportedEntity
// output.
export function extractAttdefFromGroups(
  groups: Array<{ code: number; value: string }>,
  index: number,
  spaceId: string,
): AttdefEntity | null {
  let handle = "";
  let layer = "0";
  // EPIC-COLOR-UNION S1 — accumulate the raw colour group codes (62 / 420 /
  // 430) and fold them into the typed `EntityColor` union via `colorFromDxf`,
  // matching the modern `toDrawingEntity` path. Previously only group 62 was
  // read here (true-colour / colour-book dropped in the legacy fallback).
  let colorNumber: number | undefined;
  let trueColor: number | undefined;
  let colorName: string | undefined;
  let linetype = "ByLayer";
  let lineweight = "ByLayer";
  let transparency = "0";
  let tag = "";
  let prompt = "";
  let defaultValue = "";
  let insertX = 0;
  let insertY = 0;
  let height = 2.5;
  let rotation = 0;
  let styleName = "Standard";
  let widthFactor = 1;
  let obliqueAngle = 0;
  let generationFlags = 0;
  let hJustification = 0;
  let vJustification = 0;
  let rawFlags = 0;
  let backgroundFill = 0;
  let fillBoxScale = 1.5;
  for (const g of groups) {
    switch (g.code) {
      case 5:
        handle = g.value.trim();
        break;
      case 8:
        layer = g.value;
        break;
      case 62: {
        const n = Number.parseInt(g.value, 10);
        if (Number.isFinite(n)) colorNumber = n;
        break;
      }
      case 420: {
        const n = Number.parseInt(g.value, 10);
        if (Number.isFinite(n)) trueColor = n;
        break;
      }
      case 430:
        colorName = g.value.trim();
        break;
      case 6:
        linetype = g.value;
        break;
      case 370:
        lineweight = g.value;
        break;
      case 440:
        transparency = g.value;
        break;
      case 1:
        defaultValue = g.value;
        break;
      case 2:
        tag = g.value;
        break;
      case 3:
        prompt = g.value;
        break;
      case 7:
        styleName = g.value || "Standard";
        break;
      case 10:
        insertX = Number.parseFloat(g.value) || 0;
        break;
      case 20:
        insertY = Number.parseFloat(g.value) || 0;
        break;
      case 40:
        height = Number.parseFloat(g.value) || 2.5;
        break;
      case 41:
        widthFactor = Number.parseFloat(g.value) || 1;
        break;
      case 45:
        fillBoxScale = Number.parseFloat(g.value) || 1.5;
        break;
      case 50:
        rotation = Number.parseFloat(g.value) || 0;
        break;
      case 51:
        obliqueAngle = Number.parseFloat(g.value) || 0;
        break;
      case 70:
        rawFlags = Number.parseInt(g.value, 10) || 0;
        break;
      case 71:
        generationFlags = Number.parseInt(g.value, 10) || 0;
        break;
      case 72:
        hJustification = Number.parseInt(g.value, 10) || 0;
        break;
      case 74:
        vJustification = Number.parseInt(g.value, 10) || 0;
        break;
      case 90:
        backgroundFill = Number.parseInt(g.value, 10) || 0;
        break;
      default:
        break;
    }
  }
  if (!tag && !defaultValue) return null;
  const entity: AttdefEntity = {
    id: `attdef-${index}`,
    spaceId,
    layer,
    color: colorFromDxf({ colorNumber, trueColor, colorName }),
    linetype,
    lineweight,
    transparency,
    type: "Attdef",
    handle,
    tag,
    prompt,
    defaultValue,
    insertionPoint: { x: insertX, y: insertY },
    height,
    rotation,
    styleName,
    widthFactor,
    obliqueAngle,
    generationFlags,
    hJustification,
    vJustification,
    flags: {
      invisible: (rawFlags & 1) !== 0,
      constant: (rawFlags & 2) !== 0,
      verify: (rawFlags & 4) !== 0,
      preset: (rawFlags & 8) !== 0,
      lockPosition: (rawFlags & 16) !== 0,
      multiline: (rawFlags & 32) !== 0,
    },
    backgroundFill: backgroundFill !== 0,
    fillBoxScale,
    annotativeContexts: [],
  };
  return entity;
}

// Extract ATTRIB records from an entity list following an INSERT. In
// AutoCAD DXF, when INSERT has group 66 = 1 the parser already groups
// them together as `insert.geometry.attribs`. This helper covers the
// legacy case where ATTRIBs fall through to unsupported-entity — it
// walks the group list directly. The returned array preserves
// insertion order (one ATTRIB per tag per INSERT).
export function extractAttribsFromSequence(
  groups: Array<{ code: number; value: string }>,
): { tag: string; value: string; handle: string }[] {
  const out: { tag: string; value: string; handle: string }[] = [];
  // Split the raw group list into per-entity chunks by the 0-code
  // boundary, the same way splitEntities does at the parser level.
  let current: Array<{ code: number; value: string }> | null = null;
  const chunks: Array<Array<{ code: number; value: string }>> = [];
  for (const g of groups) {
    if (g.code === 0) {
      if (current) chunks.push(current);
      current = [g];
      continue;
    }
    if (current) current.push(g);
  }
  if (current) chunks.push(current);

  for (const chunk of chunks) {
    const header = chunk[0];
    if (!header || header.value !== "ATTRIB") continue;
    let tag = "";
    let value = "";
    let handle = "";
    for (const g of chunk) {
      if (g.code === 1) value = g.value;
      else if (g.code === 2) tag = g.value.trim();
      else if (g.code === 5) handle = g.value.trim();
    }
    if (tag) out.push({ tag, value, handle });
  }
  return out;
}
